// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {getEquipmentHistory} from 'Services/equipment-service';
import Status from 'Ui/components/common/data-table/status';
import {
    formatNumberOrDefault,
    formatTimeOrDefault,
    getStatusLabelForEquipmentHistory
} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {RECOMMENDED} from 'Common/constants/service-status';
import {alphaNumericCompare, dateCompare} from 'Ui/models/maintenance';
import {PLATFORM_DATA_ADDED, FUEL_ADDED, DEFAULT_TYPES, OPTIONAL_TYPES} from 'Ui/constants/equipment-history-constants';
import {openDialog as openReduxDialog} from 'Store/actions/dialogs';
import {getStore} from 'Store/store';
import dialogTypes from 'Ui/components/common/dialog-types';
import serviceStatusConstants from 'Ui/constants/service-status-constants';
import {Tooltip, MultiSelect} from '@deere/isg.component-library';

export async function getRowData(equipment, translations) {
    if (equipment?.equipmentId) {
        const {equipmentHistory} = await getEquipmentHistory(equipment.equipmentId);
        const typeTranslationMap = new Map([...DEFAULT_TYPES, ...OPTIONAL_TYPES].map((type) => [type.key, type.translation]));

        return equipmentHistory.filter((history) => history.serviceStatus !== RECOMMENDED &&
            history.equipmentHistoryType !== PLATFORM_DATA_ADDED &&
            history.equipmentHistoryType !== FUEL_ADDED)
            .map((history) => {
                const serviceStatusOption = serviceStatusConstants[history.serviceStatus];

                return {
                    ...history,
                    type: history.equipmentHistoryType,
                    equipmentHistoryType: translations[typeTranslationMap.get(history.equipmentHistoryType)],
                    formattedStatus: getStatusLabelForEquipmentHistory(history, equipment, translations),
                    serviceType: history.serviceType === 'none' ? '' : `${history.serviceType} (${serviceStatusOption.title})`,
                    formattedTimeOfEvent: formatTimeOrDefault(history.timeOfEvent),
                    formattedTotalHours: formatNumberOrDefault(history.totalHours, 1)
                };
            });
    }

    return [];
}

export function getFilterComponent(translations) {
    const availableTypeOptions = [...DEFAULT_TYPES, ...OPTIONAL_TYPES].map(({
        key, translation
    }) => {
        return {
            id: key,
            title: translations[translation]
        };
    });

    const [typesSelected, setTypesSelected] = React.useState(DEFAULT_TYPES.map((o) => o.key));

    const typeFilterDropDown = (
        <div className={'selected-types'}>
            <MultiSelect
                autoFocus={true}
                className={'selected-types'}
                items={availableTypeOptions}
                labels={{
                    placeholder: translations.ONLINK_HISTORY_TYPE_PLACEHOLDER,
                    multiSelect: translations.ONLINK_TYPES,
                    emptyList: translations.NO_TYPES,
                    allSelect: translations.ONLINK_ALL_TYPES,
                    selectAllItems: translations.ONLINK_HISTORY_SELECT_ALL_TYPES
                }}
                name='selectedTypes'
                onChange={setTypesSelected}
                selectedIds={typesSelected}
            />
        </div>
    );

    return {
        typesSelected,
        typeFilterDropDown
    };
}

export function getColumns(translations, updateEquipment) {
    return [
        {
            headerName: translations.DATE,
            valueGetter: (_value, row) => row.timeOfEvent,
            field: 'formattedTimeOfEvent',
            width: 170,
            sortComparator: dateCompare,
            renderCell: (params) => {
                return params.row.formattedTimeOfEvent;
            }
        },
        {
            headerName: translations.TYPE,
            field: 'equipmentHistoryType',
            width: 100
        },
        {
            headerName: translations.STATUS,
            field: 'statusTo',
            valueGetter: (_value, row) => row.formattedStatus,
            width: 140,
            sortComparator: alphaNumericCompare,
            renderCell: (params) => {
                return (
                    <Status
                        formattedStatus={params.row.formattedStatus}
                        status={params.row.statusTo}
                    />
                );
            }
        },
        {
            headerName: translations.ONLINK_SERVICE,
            valueGetter: (_value, row) => row.serviceType,
            field: 'service',
            width: 260,
            sortComparator: alphaNumericCompare,
            renderCell: (params) => {
                if (params.row.serviceId) {
                    return (
                        <EditLink
                            onClick={() => {
                                getStore().dispatch(openReduxDialog(dialogTypes.SERVICE_FORM_DIALOG, {
                                    onSave: updateEquipment,
                                    serviceId: params.row.serviceId,
                                    translations
                                }));
                            }}
                        >
                            {params.row.serviceType}
                        </EditLink>
                    );
                }
                return '';
            }
        },
        {
            headerName: translations.NOTE,
            field: 'note',
            renderCell: (params) => {
                return (
                    <Tooltip
                        placement='bottom'
                        title={params.row.note}
                    >
                        <div className='basic-table-cell-block'>
                            {params.row.note}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            headerName: translations.HOURS,
            field: 'totalHours',
            type: 'number',
            minWidth: 90,
            flex: 1,
            renderCell: (params) => {
                return params.row.formattedTotalHours;
            }
        }
    ];
}
